import { useEffect, useRef } from 'react'

import { EditorView, basicSetup } from 'codemirror'
import { EditorState, StateEffect, StateField } from '@codemirror/state'
import { Decoration } from '@codemirror/view'
import { indentUnit } from '@codemirror/language'
import { python } from '@codemirror/lang-python'
import { html } from '@codemirror/lang-html'
import { css } from '@codemirror/lang-css'
import { javascript } from '@codemirror/lang-javascript'

import '../../workspace/css/workspace.scss'

const LANGUAGE_EXTENSIONS = {
  python: python(),
  html: html(),
  css: css(),
  javascript: javascript(),
}

// Optional highlight of lines that just changed (changedLines prop), as line decorations.
const setChangedLines = StateEffect.define()
const changedLinesField = StateField.define({
  create: () => Decoration.none,
  update (decorations, tr) {
    for (const effect of tr.effects) if (effect.is(setChangedLines)) return effect.value
    return decorations.map(tr.changes)
  },
  provide: field => EditorView.decorations.from(field),
})
const changedLineDecoration = Decoration.line({ attributes: { style: 'background-color: rgba(34, 197, 94, 0.18)' } })

// language defaults to 'python' for backward compatibility with the researcher-facing
// Python-only replay tool this component was originally built for (workspaceReplay2's
// ReplayWorkspace). GUI/web-dev sessions pass 'html'/'css'/'javascript' instead - see
// StudentRevisitPage, which renders one instance per file.
// changedLines: 1-based line numbers to highlight (replay change view); none by default.
// scrollable: let the viewer scroll and select the (still read-only) code. Off by default,
// where clicks must pass through to a wrapper (e.g. the click-to-edit preview in
// StudentLayoutTaskPanel).
export default function ReplayCodeEditor({ code = '', language = 'python', changedLines = null, scrollable = false }) {
  const editorRef = useRef(null)
  const viewRef = useRef(null)

  // Initialize editor
  useEffect(() => {
    if (!editorRef.current || viewRef.current) return

    const state = EditorState.create({
      doc: code,
      extensions: [
        basicSetup,
        LANGUAGE_EXTENSIONS[language] || python(),
        indentUnit.of('    '),
        changedLinesField,
        EditorState.readOnly.of(true),
        EditorView.editable.of(false),
        EditorView.theme({
          '&': { height: '100%', fontSize: '14px', color: '#1f2937' },
          '.cm-content': { padding: '16px', minHeight: '100%', fontFamily: 'Monaco, Menlo, monospace' },
          '.cm-editor': { height: '100%', backgroundColor: '#ffffff' },
          '.cm-scroller': { height: '100%', overflow: 'auto' },
          '.cm-keyword': { color: '#7c3aed' },
          '.cm-string': { color: '#059669' },
          '.cm-comment': { color: '#6b7280' },
          '.cm-number': { color: '#dc2626' },
        }),
      ],
    })

    viewRef.current = new EditorView({
      state,
      parent: editorRef.current,
    })

    return () => {
      viewRef.current?.destroy()
      viewRef.current = null
    }
  }, [])

  // Update editor content when code changes
  useEffect(() => {
    const view = viewRef.current
    if (!view) return

    const currentDoc = view.state.doc.toString()
    if (code !== currentDoc) {
      view.dispatch({
        changes: {
          from: 0,
          to: currentDoc.length,
          insert: code
        }
      })
    }
  }, [code])

  // Runs after the content effect above, so line numbers refer to the current code.
  const changedKey = changedLines ? changedLines.join(',') : ''
  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    const doc = view.state.doc
    const lines = changedKey ? changedKey.split(',').map(Number).filter(n => n >= 1 && n <= doc.lines) : []
    view.dispatch({ effects: setChangedLines.of(Decoration.set(lines.map(n => changedLineDecoration.range(doc.line(n).from)))) })
    if (!lines.length) return
    // Center the first changed line by scrolling this editor only. EditorView.scrollIntoView
    // would also scroll every scrollable ancestor (e.g. the Student Data dialog) whenever the
    // line can't be fully centered here, such as a change near the top of the file.
    const from = doc.line(lines[0]).from
    view.requestMeasure({
      read: (v) => v.lineBlockAt(from),
      write: (block, v) => {
        v.scrollDOM.scrollTop = v.documentPadding.top + block.top + block.height / 2 - v.scrollDOM.clientHeight / 2
      },
    })
  }, [changedKey, code])

  return (
    <div className="ws-editor-container" style={{ flex: 1, minHeight: 0, pointerEvents: scrollable ? 'auto' : 'none' }}>
      <div ref={editorRef} style={{ width: '100%', height: '100%' }} />
    </div>
  )
}